<script setup lang="ts">
// The user menu in the shell's foot: who's logged in, the theme (per device, ADR
// 0010), Settings, Log out and the version. It opens beside the rail on desktop and
// under the header on phones.
import { useUser } from '@itc/sdk';
import { useTheme, type ThemeChoice } from '@itc/ui';
import { computed, onBeforeUnmount, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const user = useUser();
const router = useRouter();
const version = __ITC_VERSION__;
const open = ref(false);
const root = ref<HTMLElement | null>(null);

const theme = useTheme();
const themeChoices: { value: ThemeChoice; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

const initials = computed(() => (user.user?.displayName ?? '?').slice(0, 1).toUpperCase());

async function signOut(): Promise<void> {
  open.value = false;
  await user.signOut();
  await router.push('/login');
}

const onOutside = (event: MouseEvent): void => {
  if (open.value && root.value !== null && !root.value.contains(event.target as Node)) open.value = false;
};
const onKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') open.value = false;
};
document.addEventListener('click', onOutside);
document.addEventListener('keydown', onKey);
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutside);
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <div v-if="user.user" ref="root" class="user-menu">
    <button type="button" class="user-menu__button" aria-label="Account menu" :aria-expanded="open" aria-controls="user-menu-panel" @click="open = !open">
      <img v-if="user.user.avatarUrl" :src="user.user.avatarUrl" alt="" class="user-menu__avatar" />
      <span v-else class="user-menu__avatar user-menu__avatar--initials" aria-hidden="true">{{ initials }}</span>
      <span class="user-menu__name">{{ user.user.displayName }}</span>
    </button>
    <div v-if="open" id="user-menu-panel" class="user-menu__panel">
      <div class="user-menu__who">
        <strong>{{ user.user.displayName }}</strong>
        <span class="user-menu__meta">@{{ user.user.username }}</span>
      </div>
      <div class="user-menu__theme" role="group" aria-label="Theme">
        <span class="user-menu__theme-label">Theme</span>
        <button
          v-for="option in themeChoices"
          :key="option.value"
          type="button"
          class="user-menu__theme-option"
          :aria-pressed="theme.choice.value === option.value"
          @click="theme.set(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
      <RouterLink to="/settings" class="user-menu__item" @click="open = false">Settings</RouterLink>
      <button type="button" class="user-menu__item" @click="signOut">Log out</button>
      <div class="user-menu__version">Version {{ version }}</div>
    </div>
  </div>
</template>

<style scoped>
.user-menu {
  position: relative;
}
.user-menu__button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 100%;
  min-height: var(--itc-tap);
  padding: 4px 2px;
  border: 0;
  border-radius: var(--itc-r-control);
  background: none;
  color: var(--itc-muted);
  cursor: pointer;
}
.user-menu__button:hover,
.user-menu__button[aria-expanded='true'] {
  color: var(--itc-fg);
  background: var(--itc-panel-2);
}
.user-menu__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}
.user-menu__avatar--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--itc-accent-soft);
  color: var(--itc-accent);
  font-family: var(--itc-font-display);
  font-size: 13px;
  font-weight: 700;
}
.user-menu__name {
  max-width: 100%;
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-menu__panel {
  position: absolute;
  bottom: 0;
  left: calc(100% + 16px);
  z-index: 40;
  width: 260px;
  padding: 6px;
  background: var(--itc-panel);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-control);
  box-shadow: var(--itc-shadow-pop);
}
.user-menu__who {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--itc-s2) var(--itc-s3);
}
.user-menu__meta,
.user-menu__version {
  color: var(--itc-muted);
  font-family: var(--itc-font-mono);
  font-size: 12px;
}
.user-menu__version {
  padding: var(--itc-s2) var(--itc-s3);
  color: var(--itc-faint);
  border-top: 1px solid var(--itc-line);
}
.user-menu__theme {
  display: flex;
  align-items: center;
  gap: var(--itc-s1);
  padding: var(--itc-s2) var(--itc-s3);
  border-top: 1px solid var(--itc-line);
  border-bottom: 1px solid var(--itc-line);
}
.user-menu__theme-label {
  flex: 1;
  color: var(--itc-muted);
  font-size: 13px;
}
.user-menu__theme-option {
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-pill);
  background: transparent;
  color: var(--itc-muted);
  font-size: 13px;
  cursor: pointer;
}
.user-menu__theme-option[aria-pressed='true'] {
  border-color: var(--itc-accent);
  background: var(--itc-accent-soft);
  color: var(--itc-fg);
}
.user-menu__item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: var(--itc-tap);
  padding: 0 var(--itc-s3);
  border: 0;
  border-radius: var(--itc-r-input);
  background: transparent;
  color: var(--itc-fg);
  font: inherit;
  font-weight: 700;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}
.user-menu__item:hover {
  background: var(--itc-panel-2);
}
@media (max-width: 720px) {
  .user-menu__button {
    width: var(--itc-tap);
  }
  .user-menu__name {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .user-menu__panel {
    position: fixed;
    top: calc(var(--itc-bar-height) + 4px);
    right: 8px;
    bottom: auto;
    left: auto;
    width: min(300px, calc(100vw - 16px));
  }
}
</style>
